import React, { useState, useEffect, useRef } from "react";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import { Trash2, Search } from "react-feather";
import "./AdminPanel.css";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import streamlineIconLogo from "./assets/Navbar_icon_logo.svg";
import streamlineTextLogo from "./assets/Navbar_text_logo.svg";

const API_BASE_URL = (process.env.REACT_APP_BASE_URL || "http://127.0.0.1:8123").replace(/\/$/, "");
const API_ENDPOINTS = {
  getConfig: `${API_BASE_URL}/api/admin/configuration`,
  updateConfig: `${API_BASE_URL}/api/admin/configuration/update`,
  addInstitute: `${API_BASE_URL}/api/admin/institutes/add`,
  deleteInstitute: `${API_BASE_URL}/api/admin/institutes/delete`,
};

const FIELD_DEFAULTS = {
  projectEarliestStartDate: ["Earliest Start Date: "],
  budgets: [
    "Phase I Budget Cap: ",
    "Phase II Budget Cap: ",
    "Federal Salary Cap: ",
    "Equipment Threshold: ",
  ],
  performancePeriods: ["Phase I: ", "Phase II: "],
  nofoNumbers: ["", "", "", ""],
  headers: ["DMSP Header: ", "Bio Sketch Header: "],
  criticalDates: ["Budget waiver publication date: "],
  institutes: [""],
};

function parseEntry(entry) {
  if (typeof entry === "object" && entry !== null) {
    return { name: entry.name || "", value: entry.value || "" };
  }
  if (typeof entry !== "string") return { name: "", value: "" };
  const idx = entry.indexOf(":");
  if (idx === -1) return { name: entry, value: "" };
  return { name: entry.slice(0, idx).trim(), value: entry.slice(idx + 1).trim() };
}

function isCurrencyField(name) {
  return /budget cap|salary|threshold/i.test(name);
}

function formatCurrency(value) {
  const numericValue = value.replace(/[^0-9.]/g, "");
  if (numericValue) {
    const parts = numericValue.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  }
  return "";
}

function removeCurrencyFormatting(value) {
  return value.replace(/[$,]/g, "");
}

function formatToMonthDay(dateStr) {
  if (!dateStr) return "";
  if (/^[A-Za-z]+\s+\d{1,2},\s*\d{4}$/.test(dateStr.trim())) return dateStr.trim();
  if (/^[A-Za-z]+\s+\d{1,2}$/.test(dateStr.trim())) return dateStr.trim();
  const match = dateStr.trim().match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (match) {
    const [, month, day, year] = match;
    const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  }
  const isoMatch = dateStr.trim().match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  }
  return dateStr;
}

function formatDateTime(timestamp) {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

function convertApiToLocal(apiData) {
  if (!apiData) return null;
  const cfg = apiData.configData || apiData;
  const toEntry = (obj) => {
    if (typeof obj === "string") return obj;
    if (!obj) return "";
    return `${obj.name}: ${obj.value}`;
  };
  const criticalDatesRaw = (cfg.criticalDates || []).map((e) => {
    const name = typeof e === "string" ? e : e.name || "";
    const value = typeof e === "string" ? "" : e.value || "";
    const canonicalName = /budget\s*waiver/i.test(name) ? "Budget waiver publication date" : name;
    return { name: canonicalName, value };
  });
  const criticalDatesMap = new Map();
  criticalDatesRaw.forEach((cd) => criticalDatesMap.set(cd.name, `${cd.name}: ${cd.value}`));
  const criticalDates = Array.from(criticalDatesMap.values());

  const rawNofoNumbers = (cfg.nofoNumbers || []).map((num) => String(num || "").trim()).filter((num) => num !== "");
  const uniqueMap = new Map();
  rawNofoNumbers.forEach((num) => uniqueMap.set(num, num));
  const last4 = Array.from(uniqueMap.values()).slice(-4);
  const nofoNumbers = Array(4).fill("").map((_, i) => last4[i] || "");

  return {
    projectEarliest: (cfg.projectEarliestStartDate || []).map(toEntry),
    budgets: (cfg.budgets || []).map(toEntry),
    performancePeriods: (cfg.performancePeriods || []).map(toEntry),
    nofoNumbers,
    headers: (cfg.headers || []).map(toEntry),
    criticalDates,
    institutes: (cfg.institutes || []).map((i) => i.abbreviation),
  };
}

// ===== INSTITUTE SELECTOR MODAL =====
const InstituteSelectorModal = ({ selected, setSelected, allInstitutes, setAllInstitutes, loading, userName }) => {
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newAbbr, setNewAbbr] = useState("");
  const [newFullName, setNewFullName] = useState("");
  const [addSuccess, setAddSuccess] = useState("");
  const [tempSelected, setTempSelected] = useState(selected);

  const handleCancel =() => { setShowModal(false); setSearchTerm(""); setNewAbbr(""); setNewFullName(""); setAddSuccess(""); };
  const handleSave = () => { setSelected(tempSelected); setShowModal(false); setSearchTerm(""); setNewAbbr(""); setNewFullName(""); setAddSuccess(""); };
  const handleToggle = (value) => {
    setTempSelected(tempSelected.includes(value) ? tempSelected.filter((v) => v !== value) : [...tempSelected, value]);
  };

  const handleAddNew = async () => {
    const abbr = newAbbr.trim().toUpperCase();
    const fullName = newFullName.trim();
    if (!abbr || !fullName) return;
    if (allInstitutes.find((inst) => (inst.value || "").toLowerCase() === abbr.toLowerCase())) {
      alert("Institute abbreviation already exists!"); return;
    }
    try {
      const response = await fetch(API_ENDPOINTS.addInstitute, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ user: userName || "Admin", abbreviation: abbr, full_name: fullName }),
      });
      let data = null;
      try { data = await response.json(); } catch (_) {}
      if (!response.ok) {
        const msg = data?.error || data?.message || `HTTP ${response.status}`;
        alert(`Failed to add institute: ${msg}`); return;
      }
      setAllInstitutes((prev) => [...prev, { value: abbr, label: fullName }]);
      setSelected((prev) => [...prev, abbr]);
      setAddSuccess(`"${abbr} - ${fullName}" added!`);
      setNewAbbr(""); setNewFullName("");
      setTimeout(() => setAddSuccess(""), 2000);
    } catch (err) { alert("Error adding institute: " + err.message); }
  };

  const handleRemove = async (value) => {
    if (!window.confirm(`Are you sure you want to remove "${value}"?`)) return;
    try {
      const response = await fetch(API_ENDPOINTS.deleteInstitute, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: userName, abbreviation: value }),
      });
      const data = await response.json();
      if (!response.ok) { alert("Failed to delete: " + (data?.error || data?.message || "Unknown error")); return; }
      setAllInstitutes(allInstitutes.filter((inst) => inst.value !== value));
      setTempSelected(tempSelected.filter((v) => v !== value));
    } catch (err) { alert("Error deleting institute: " + err.message); }
  };

  const filtered = allInstitutes.filter(
    (inst) =>
      (inst.value || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inst.label || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Modal show={showModal} onHide={handleCancel} centered size="lg" scrollable>
        <Modal.Header closeButton><Modal.Title>Select Institutes</Modal.Title></Modal.Header>
        <Modal.Body>
          {addSuccess && <div className="alert alert-success py-1 mb-2">{addSuccess}</div>}
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text><Search size={14} /></InputGroup.Text>
            <Form.Control placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </InputGroup>
          <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #eee", borderRadius: 6 }}>
            <div className="d-flex py-2" style={{ background: "#f8f9fa", fontWeight: 500, borderBottom: "1px solid #eaeaea" }}>
              <div style={{ width: 90, paddingLeft: 10 }}></div>
              <div style={{ flex: 1 }}>Full Name</div>
              <div style={{ width: 80 }}></div>
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "#6c757d" }}>No institutes found</div>
            ) : (
              filtered.map((inst) => (
                <div key={inst.value} className="d-flex align-items-center"
                  style={{ borderBottom: "1px solid #f8f9fa", padding: "8px 0", backgroundColor: tempSelected.includes(inst.value) ? "#f5f5f5" : "#fff", cursor: "pointer" }}
                  onClick={() => handleToggle(inst.value)}>
                  <Form.Check type="checkbox" checked={tempSelected.includes(inst.value)} onChange={() => handleToggle(inst.value)} onClick={(e) => e.stopPropagation()} style={{ marginRight: 8, marginLeft: 12 }} />
                  <div style={{ width: 70, fontWeight: 500 }}>{inst.value}</div>
                  <div style={{ flex: 1, fontSize: 14 }}>{inst.label}</div>
                  <Button variant="link" size="sm" style={{ padding: 0, color: "#6c757d" }}
                    onClick={(e) => { e.stopPropagation(); handleRemove(inst.value); }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#dc3545")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#6c757d")}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))
            )}
          </div>
          <div className="d-flex align-items-end gap-2 mt-4">
            <Form.Group style={{ width: 110 }}>
              <Form.Control size="sm" maxLength={12} placeholder="e.g. NIH" value={newAbbr} onChange={(e) => setNewAbbr(e.target.value)} />
            </Form.Group>
            <Form.Group className="flex-grow-1">
              <Form.Control size="sm" placeholder="e.g. National Institutes of Health" value={newFullName} onChange={(e) => setNewFullName(e.target.value)} />
            </Form.Group>
            <Button variant="primary" size="sm" style={{ height: 36, marginTop: 15 }} onClick={handleAddNew} disabled={!newAbbr.trim() || !newFullName.trim()}>Add New</Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div style={{ flex: 1, fontSize: 14, color: "#6c757d" }}>{tempSelected.length} selected</div>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>OK</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// ===== EDIT INSTITUTES MODAL =====
const EditInstitutesModal = ({ allInstitutes, setAllInstitutes, selected, setSelected, loading, userName }) => {
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newAbbr, setNewAbbr] = useState("");
  const [newFullName, setNewFullName] = useState("");
  const [addSuccess, setAddSuccess] = useState("");

  const handleClose = () => { setShowModal(false); setSearchTerm(""); setNewAbbr(""); setNewFullName(""); setAddSuccess(""); };

  const handleAddNew = async () => {
    const abbr = newAbbr.trim().toUpperCase();
    const fullName = newFullName.trim();
    if (!abbr || !fullName) return;
    if (allInstitutes.find((inst) => (inst.value || "").toLowerCase() === abbr.toLowerCase())) {
      alert("Institute abbreviation already exists!"); return;
    }
    try {
      const response = await fetch(API_ENDPOINTS.addInstitute, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ user: userName, abbreviation: abbr, full_name: fullName }),
      });
      let data = null;
      try { data = await response.json(); } catch (_) {}
      if (!response.ok) { alert(`Failed to add: ${data?.error || data?.message || `HTTP ${response.status}`}`); return; }
      setAllInstitutes((prev) => [...prev, { value: abbr, label: fullName }]);
      setAddSuccess(`"${abbr} - ${fullName}" added!`);
      setNewAbbr(""); setNewFullName("");
      setTimeout(() => setAddSuccess(""), 2000);
    } catch (err) { alert("Error: " + err.message); }
  };

  const handleRemove = async (value) => {
    if (!window.confirm(`Remove "${value}"?`)) return;
    try {
      const response = await fetch(API_ENDPOINTS.deleteInstitute, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: userName, abbreviation: value }),
      });
      const data = await response.json();
      if (!response.ok) { alert("Failed to delete: " + (data?.error || data?.message || "Unknown error")); return; }
      setAllInstitutes(allInstitutes.filter((inst) => inst.value !== value));
      setSelected((prev) => prev.filter((v) => v !== value));
    } catch (err) { alert("Error: " + err.message); }
  };

  const filtered = allInstitutes.filter(
    (inst) =>
      (inst.value || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inst.label || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Button variant="outline-primary" size="sm" style={{ marginLeft: 8, height: 38, minWidth: 120 }} onClick={() => setShowModal(true)}>
        Edit Institutes
      </Button>
      <Modal show={showModal} onHide={handleClose} centered size="lg" scrollable>
        <Modal.Header closeButton><Modal.Title>Edit Institutes</Modal.Title></Modal.Header>
        <Modal.Body>
          {addSuccess && <div className="alert alert-success py-1 mb-2">{addSuccess}</div>}
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text><Search size={14} /></InputGroup.Text>
            <Form.Control placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </InputGroup>
          <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #eee", borderRadius: 6 }}>
            <div className="d-flex py-2" style={{ background: "#f8f9fa", fontWeight: 500, borderBottom: "1px solid #eaeaea" }}>
              <div style={{ width: 90, paddingLeft: 10 }}>Abbr.</div>
              <div style={{ flex: 1 }}>Full Name</div>
              <div style={{ width: 80, textAlign: "center" }}>Action</div>
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "#6c757d" }}>No institutes found</div>
            ) : (
              filtered.map((inst) => (
                <div key={inst.value} className="d-flex align-items-center" style={{ borderBottom: "1px solid #f8f9fa", padding: "8px 10px" }}>
                  <div style={{ width: 90, fontWeight: 500 }}>{inst.value}</div>
                  <div style={{ flex: 1, fontSize: 14 }}>{inst.label}</div>
                  <div style={{ width: 80, textAlign: "center" }}>
                    <Button variant="link" size="sm" style={{ padding: 0, color: "#6c757d" }}
                      onClick={() => handleRemove(inst.value)}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "#dc3545")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "#6c757d")}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="d-flex align-items-end gap-2 mt-4">
            <Form.Group style={{ width: 110 }}>
              <Form.Control size="sm" maxLength={12} placeholder="e.g. NIH" value={newAbbr} onChange={(e) => setNewAbbr(e.target.value)} />
            </Form.Group>
            <Form.Group className="flex-grow-1">
              <Form.Control size="sm" placeholder="e.g. National Institutes of Health" value={newFullName} onChange={(e) => setNewFullName(e.target.value)} />
            </Form.Group>
            <Button variant="primary" size="sm" style={{ height: 38 }} onClick={handleAddNew} disabled={!newAbbr.trim() || !newFullName.trim()}>Add New</Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div style={{ flex: 1, fontSize: 14, color: "#6c757d" }}>Total: {allInstitutes.length} institutes</div>
          <Button variant="primary" onClick={handleClose}>Close</Button>
        </Modal.Footer>
      </Modal>
      <InstituteSelectorModal selected={selected} setSelected={setSelected} allInstitutes={allInstitutes} setAllInstitutes={setAllInstitutes} loading={loading} userName={userName} />
    </>
  );
};

// ===== SECTION =====
const Section = ({ title, sectionKey, data, handleValueChange, setData, allInstitutes, setAllInstitutes, institutesLoading, userName }) => {
  const getInstituteDisplay = () => {
    const list = Array.isArray(data?.institutes) ? data.institutes.filter((x) => x && x.toLowerCase() !== "string") : [];
    if (list.length === 0) return "No institutes selected";
    return `${list.length} institutes selected`;
  };

  const handleCurrencyChange = (section, index, newValue) => {
    const clean = removeCurrencyFormatting(newValue);
    handleValueChange(section, index, clean ? `$${formatCurrency(clean)}` : "");
  };

  const handleDateChange = (section, index, newValue) => {
    if (!newValue) { handleValueChange(section, index, ""); return; }
    const [year, month, day] = newValue.split("-");
    handleValueChange(section, index, `${month}/${day}/${year}`);
  };

  const formatDateForInput = (value, section) => {
    if (!value || value.trim() === "") return "2025-12-01";
    const trimmed = value.trim();
    if (section === "projectEarliest") {
      const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
      const mWithYear = trimmed.match(/^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/);
      if (mWithYear) {
        const idx = monthNames.findIndex((mn) => mn.toLowerCase() === mWithYear[1].toLowerCase());
        if (idx !== -1) return `${mWithYear[3]}-${String(idx + 1).padStart(2, "0")}-${String(mWithYear[2]).padStart(2, "0")}`;
      }
      const m = trimmed.match(/^([A-Za-z]+)\s+(\d{1,2})$/);
      if (m) {
        const idx = monthNames.findIndex((mn) => mn.toLowerCase() === m[1].toLowerCase());
        if (idx !== -1) return `${new Date().getFullYear()}-${String(idx + 1).padStart(2, "0")}-${String(m[2]).padStart(2, "0")}`;
      }
    }
    const d = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (d) return `${d[3]}-${d[1].padStart(2, "0")}-${d[2].padStart(2, "0")}`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    return "2025-12-01";
  };

  return (
    <Card className="mb-4 admin-card">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4 className="mb-0">{title}</h4>
        </div>
        {sectionKey === "institutes" ? (
          <div className="d-flex align-items-center justify-content-between" style={{ width: "100%" }}>
            <div style={{ border: "1px solid #ced4da", borderRadius: 9, padding: "8px 12px", backgroundColor: "#f8f9fa", minHeight: 38, minWidth: 350, maxWidth: 400, color: data.institutes.length === 0 ? "#6c757d" : "#000", fontSize: 14, pointerEvents: "none", userSelect: "none" }}>
              {getInstituteDisplay()}
            </div>
            <EditInstitutesModal allInstitutes={allInstitutes} setAllInstitutes={setAllInstitutes} selected={data.institutes} setSelected={(updated) => setData((prev) => ({ ...prev, institutes: typeof updated === "function" ? updated(prev.institutes) : updated }))} loading={institutesLoading} userName={userName} />
          </div>
        ) : sectionKey === "nofoNumbers" ? (
          data.nofoNumbers.map((item, index) => (
            <div key={index} className="d-flex align-items-center mb-2">
              <Form.Control type="text" value={item} onChange={(e) => handleValueChange("nofoNumbers", index, e.target.value)} className="admin-input" style={{ maxWidth: 400, minWidth: 350 }} />
            </div>
          ))
        ) : (
          data[sectionKey].map((item, index) => {
            const { name, value } = parseEntry(item);
            const isCurrency = isCurrencyField(name);
            const isDateField = sectionKey === "projectEarliest" || sectionKey === "criticalDates";
            return (
              <div key={index} className="d-flex align-items-center mb-2">
                <Form.Control type="text" value={name} className="admin-input me-2" readOnly style={{ background: "#f1f3f6", fontWeight: 500, maxWidth: 350 }} />
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                  {isCurrency ? (
                    <InputGroup style={{ maxWidth: 400, minWidth: 350 }}>
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control type="text" value={removeCurrencyFormatting(value)} onChange={(e) => handleCurrencyChange(sectionKey, index, e.target.value)} placeholder="0" />
                    </InputGroup>
                  ) : isDateField ? (
                    <Form.Control type="date" value={formatDateForInput(value, sectionKey)} onChange={(e) => handleDateChange(sectionKey, index, e.target.value)} className="admin-input" style={{ maxWidth: 400, minWidth: 350 }} />
                  ) : (
                    <Form.Control type="text" value={value} onChange={(e) => handleValueChange(sectionKey, index, e.target.value)} className="admin-input" style={{ maxWidth: 400, minWidth: 350 }} />
                  )}
                </div>
              </div>
            );
          })
        )}
      </Card.Body>
    </Card>
  );
};

// ===== ADMIN PANEL =====
const AdminPanel = () => {
  const [data, setData] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allInstitutes, setAllInstitutes] = useState([]);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const hasFetchedRef = useRef(false);

  const userName = "Admin";

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const res = await fetch(API_ENDPOINTS.getConfig, { headers: { accept: "application/json" } });
      if (!res.ok) throw new Error(`Fetch failed: ${res.status} ${res.statusText}`);
      const json = await res.json();
      if (!json?.configData) throw new Error("Invalid configuration response");
      if (json.configData.institutes) {
        setAllInstitutes(json.configData.institutes.map((inst) => ({ value: inst.abbreviation, label: inst.fullName })));
      }
      const local = convertApiToLocal(json);
      if (!local) throw new Error("Failed to convert configuration");
      setData(local);
      setAuditLog(json.auditLog || []);
      setApiLoaded(true);
    } catch (err) {
      setFetchError(err.message || "Error fetching configuration");
      setApiLoaded(false);
      hasFetchedRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchConfig();
  }, []);

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    if (apiLoaded && data) {
      setData((prev) => {
        const merged = { ...prev };
        Object.keys(FIELD_DEFAULTS).forEach((section) => {
          const arr = prev[section] || [];
          if (arr.length < FIELD_DEFAULTS[section].length) {
            merged[section] = [...arr, ...FIELD_DEFAULTS[section].slice(arr.length)];
          }
        });
        if (!merged.projectEarliest || merged.projectEarliest.length === 0) merged.projectEarliest = ["Earliest Start Date: "];
        if (!merged.nofoNumbers || merged.nofoNumbers.length < 4) {
          merged.nofoNumbers = Array(4).fill("").map((_, i) => merged.nofoNumbers?.[i] || "");
        }
        return merged;
      });
    }
  }, [apiLoaded]);

  const handleValueChange = (section, index, newValue) => {
    setData((prev) => {
      const updated = [...prev[section]];
      if (section === "projectEarliest") {
        const old = parseEntry(updated[index]);
        updated[index] = `${old.name}: ${formatToMonthDay(newValue)}`;
      } else if (section === "nofoNumbers") {
        const copy = [...prev[section]];
        copy[index] = newValue;
        return { ...prev, [section]: copy };
      } else {
        const old = parseEntry(updated[index]);
        updated[index] = `${old.name}: ${newValue}`;
      }
      return { ...prev, [section]: updated };
    });
  };

  const confirmSaveChanges = async () => {
    setLoading(true);
    setShowConfirmModal(false);
    const nofoObject = {};
    (data.nofoNumbers || []).forEach((num) => { const n = String(num || "").trim(); if (n) nofoObject[n] = n; });
    const payload = {
      user: userName,
      changes: {
        projectEarliestStartDate: Object.fromEntries(data.projectEarliest.map((e) => { const { name, value } = parseEntry(e); return [name, value]; })),
        budgets: Object.fromEntries(data.budgets.map((e) => { const { name, value } = parseEntry(e); return [name, value]; })),
        performancePeriods: Object.fromEntries(data.performancePeriods.map((e) => { const { name, value } = parseEntry(e); return [name, value]; })),
        nofoNumbers: nofoObject,
        headers: Object.fromEntries(data.headers.map((e) => { const { name, value } = parseEntry(e); return [name, value]; })),
        criticalDates: Object.fromEntries(data.criticalDates.map((e) => { const { name, value } = parseEntry(e); return [name, value]; })),
      },
    };
    try {
      const res = await fetch(API_ENDPOINTS.updateConfig, {
        method: "POST",
        headers: { "Content-Type": "application/json", accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status}): ${await res.text()}`);
      const responseJson = await res.json();
      if (!responseJson.success) { alert("Failed to save: " + (responseJson.errors?.join(", ") || "Unknown error")); setLoading(false); return; }
      hasFetchedRef.current = false;
      await fetchConfig();
      alert("Changes saved successfully!");
    } catch (err) {
      alert("Error saving changes: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !apiLoaded) {
    return (
      <div style={{ textAlign: "center", marginTop: 100 }}>
        <Spinner animation="border" />
        <div style={{ marginTop: 12 }}>Loading Admin Panel...</div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div style={{ maxWidth: 800, margin: "60px auto", padding: "0 16px" }}>
        <div className="alert alert-danger"><strong>Failed to load:</strong> {fetchError}</div>
        <Button variant="primary" onClick={() => { hasFetchedRef.current = false; fetchConfig(); }}>Retry</Button>
      </div>
    );
  }

  if (!apiLoaded || !data) return null;

  return (
    <div style={{ minHeight: "100vh", background: "#87ceeb", fontFamily: "'Inter', 'SF Pro', sans-serif" }}>
      {/* Navbar matching Dashboard style */}
      <div style={{ background: "white", padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", position: "sticky", top: 0, zIndex: 999 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={streamlineIconLogo} alt="Streamline" style={{ height: 28 }} />
          <img src={streamlineTextLogo} alt="Streamline" style={{ height: 18 }} />
        </div>
        <div style={{ display: "flex", gap: 24 }}>
          <a href="/dashboard" style={{ textDecoration: "none", color: "#374151", fontWeight: 500, fontSize: 15 }}>Dashboard</a>
          <a href="/admin" style={{ textDecoration: "none", color: "#374151", fontWeight: 600, fontSize: 15, borderBottom: "2px solid #374151" }}>Admin Panel</a>
        </div>
      </div>

      <div className="admin-form-content">
        <h2 style={{ fontWeight: 700, color: "#1a1a1a" }}>Configurable Admin Panel</h2>
        <form onSubmit={(e) => { e.preventDefault(); setShowConfirmModal(true); }}>
          {[
            { title: "Project Earliest", key: "projectEarliest" },
            { title: "Budget & Financials", key: "budgets" },
            { title: "Omnibus Performance Periods", key: "performancePeriods" },
            { title: "Omnibus NOFO Numbers", key: "nofoNumbers" },
            { title: "Headers & Compliance", key: "headers" },
            { title: "Critical Dates", key: "criticalDates" },
            { title: "Institutes", key: "institutes" },
          ].map(({ title, key }) => (
            <Section key={key} title={title} sectionKey={key} data={data} handleValueChange={handleValueChange} setData={setData} allInstitutes={allInstitutes} setAllInstitutes={setAllInstitutes} institutesLoading={false} userName={userName} />
          ))}
          <div className="d-flex justify-content-end mt-3 mb-4">
            <Button variant="primary" type="submit" style={{ minWidth: 140 }}>Save Changes</Button>
          </div>
        </form>

        {/* Audit Log */}
        <Card className="admin-audit-log-card mb-4">
          <Card.Body>
            <h5 className="mb-3">Audit Log</h5>
            <div className="admin-audit-log">
              {auditLog.length === 0 ? (
                <p style={{ color: "#6c757d" }}>No edits recorded yet.</p>
              ) : (
                auditLog.map((entry, index) => {
                  const user = entry.user || entry.userName || entry.username || "Unknown";
                  const clean = (v) => (v ? String(v).trim() : "");
                  const newVal = clean(entry.newValue);
                  const oldVal = clean(entry.oldValue);
                  const sectionNamesMap = { projectearliest: "Project Earliest", budgets: "Budget & Financials", performanceperiods: "Omnibus Performance Periods", nofonumbers: "Omnibus NOFO Numbers", headers: "Headers & Compliance", criticaldates: "Critical Dates", institutes: "Institutes" };
                  const sectionKey = (entry.section || "").trim().toLowerCase();
                  const section = sectionNamesMap[sectionKey] || entry.section || "General";
                  const isInstitute = section.toLowerCase() === "institutes";
                  let text = null;
                  if (isInstitute) {
                    if (!oldVal && newVal) text = <><span className="fw-bold">{user}</span> added <span className="text-success">{newVal}</span> in <span className="fw-bold">Institutes</span></>;
                    else if (oldVal && !newVal) text = <><span className="fw-bold">{user}</span> deleted <span className="text-danger">{oldVal}</span> from <span className="fw-bold">Institutes</span></>;
                  } else {
                    text = <><span className="fw-bold">{user}</span> updated <span className="fw-bold">{entry.field}</span> in <span className="fw-bold">{section}</span><br />from: <span className="text-danger">{oldVal || "—"}</span> to: <span className="text-success">{newVal || "—"}</span></>;
                  }
                  if (!text) return null;
                  return (
                    <div key={index} className="mb-3 text-start">
                      {text}<br />
                      <span className="text-muted" style={{ fontSize: "0.85em" }}>{formatDateTime(entry.time)}</span>
                    </div>
                  );
                })
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Confirm Save Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirm Save</Modal.Title></Modal.Header>
        <Modal.Body>Are you sure you want to save these changes?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={confirmSaveChanges}>
            {loading ? <Spinner animation="border" size="sm" /> : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminPanel;
